import { Context } from 'chartjs-plugin-datalabels';
import { useMemo, useState } from 'react';

import { FinancialChartData } from '~hooks/query/company/useCompanyFinancialChart';
import { setGtmDataLayer } from '~utils/common/thirdPartyEvent';
import { getTransformMoneyUnit } from '~utils/formatted/string';

import {
  DEFAULT_CHART_STYLE,
  DEFAULT_OPTIONS,
  LINE_STYLE,
  POINT_STYLE,
  TOOLTIP_CONNECT_DASH,
  TOOLTIP_LINE_PLUGINS,
} from './constants';

interface UseChartProps {
  originData: FinancialChartData[];
  externalTooltipHandler: (context: Context) => void;
  isEmpty?: boolean;
}

export function useChartProps({ originData, externalTooltipHandler, isEmpty }: UseChartProps) {
  const [isInteracted, setIsInteracted] = useState<boolean>();
  const labels = [2019, 2020, 2021];

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          type: 'line',
          label: '전체인원',
          data: originData,
          parsing: {
            xAxisKey: 'year',
            yAxisKey: 'salesAmount',
          },
          ...LINE_STYLE,
          ...POINT_STYLE,
        },
        {
          type: 'bar',
          label: '영업이익',
          data: originData,
          parsing: {
            xAxisKey: 'year',
            yAxisKey: 'operatingIncome',
          },
          backgroundColor: '#87519B',
          ...DEFAULT_CHART_STYLE,
        },
        {
          type: 'bar',
          label: '당기순이익',
          data: originData,
          parsing: {
            xAxisKey: 'year',
            yAxisKey: 'netIncome',
          },
          backgroundColor: '#F8DDC7',
          ...DEFAULT_CHART_STYLE,
        },
      ],
    }),
    [originData, labels],
  );

  const options = useMemo(
    () => ({
      ...DEFAULT_OPTIONS,
      onHover: () => {
        if (!isInteracted) {
          setIsInteracted(true);
        }
      },
      onClick: (ctx: any) => {
        if (!isInteracted) {
          setIsInteracted(true);
        }

        const [salesAmountPoint] = ctx.chart.tooltip.dataPoints;

        if (salesAmountPoint) {
          const revenue = salesAmountPoint.parsed.y;
          const year = salesAmountPoint.label;

          setGtmDataLayer('company__revenue__graph__click', {
            revenue,
            year,
          });
        }
      },
      plugins: {
        ...DEFAULT_OPTIONS.plugins,
        tooltip: {
          enabled: false,
          external: externalTooltipHandler,
        },
      },
      scales: {
        x: {
          ...DEFAULT_OPTIONS.scales.x,
          grid: {
            ...DEFAULT_OPTIONS.scales.x.grid,
          },
          ticks: {
            ...DEFAULT_OPTIONS.scales.x.ticks,
          },
        },
        y: {
          ...DEFAULT_OPTIONS.scales.y,
          ticks: {
            display: !isEmpty,
            callback(value: number) {
              return getTransformMoneyUnit(value, true);
            },
          },
        },
      },
    }),
    [labels, isEmpty, externalTooltipHandler],
  );

  const plugins = [TOOLTIP_LINE_PLUGINS, TOOLTIP_CONNECT_DASH];

  return { data, options, plugins, isInteracted };
}
